import React, { useState, useEffect } from 'react';
import { Clock, ChartBarIcon, Download, FileText } from 'lucide-react';
import { payrollAPI } from '../services/employee.service';
import ActivityLogs from '../components/ActivityLogs';
import { useAuditLog } from '../hooks/useAuditLog';

const SalaryStructureBuilder = () => {
  const [activeTab, setActiveTab] = useState('payroll');
  const [loading, setLoading] = useState(false);
  const [payslipRequests, setPayslipRequests] = useState([]);
  
  // Salary Structure Builder specific activity types
  const activityTypes = [
    "Payslip Generated", "Bulk Payslips Generated", "Tax Regime Selected", 
    "Payslip Request Approved", "Payslip Request Rejected", "Salary Structure Updated",
    "Payroll Processed", "Employee Salary Modified", "Deduction Applied", "Bonus Added"
  ];

  // Salary Structure Builder specific report types
  const reportTypes = [
    "Payroll Summary Report", "Salary Structure Report", "Tax Regime Report",
    "Payslip Generation Report", "Employee Compensation Report", "Deductions Report",
    "Bonus & Incentives Report", "Payroll Processing Report"
  ];
  
  // Audit logging hook
  const { logCreate } = useAuditLog('Payroll Management');
  
  // Form states
  const [generateForm, setGenerateForm] = useState({
    emp_code: '',
    month: '',
    year: new Date().getFullYear()
  });
  
  const [bulkForm, setBulkForm] = useState({
    month: '',
    year: new Date().getFullYear(),
    emp_codes: []
  });
  
  const [taxRegimeForm, setTaxRegimeForm] = useState({
    tax_regime: 'NEW',
    financial_year: '2024-2025'
  });

  const months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  useEffect(() => {
    fetchPayslipRequests();
  }, []);

  const fetchPayslipRequests = async () => {
    try {
      const response = await payrollAPI.getPayslipRequests();
      const data = response.data.data || response.data || [];
      setPayslipRequests(data);
    } catch (error) {
      console.error('Error fetching payslip requests:', error);
    }
  };

  const handleGeneratePayslip = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await payrollAPI.generatePayslip(generateForm);
      console.log('Generate payslip response:', response);
      
      // Check if response indicates success (HTTP 200 OR success flag)
      if (response.status === 200 || response.data?.success === true || response.success === true) {
        alert('✅ Payslip generated successfully! Check PayrollSettings for breakdown.');
        setGenerateForm({ emp_code: '', month: '', year: new Date().getFullYear() });
        
        // Log the activity
        logCreate('Individual Payslip Generated', `Generated payslip for ${generateForm.emp_code} - ${generateForm.month} ${generateForm.year}`);
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error occurred';
        alert(`❌ Error generating payslip: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Generate payslip error:', error);
      
      // Handle network timeout/error gracefully
      if (!error.response) {
        alert('⚠️ Request may have timed out, but the operation likely completed successfully. Please check PayrollSettings.');
      } else {
        const errorMsg = error.response.data?.message || error.response.data?.error || error.message || 'Network error occurred';
        alert(`❌ Error generating payslip: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await payrollAPI.generateBulkPayslips(bulkForm);
      console.log('Bulk generate response:', response);
      
      // Check if response indicates success (HTTP 200 OR success flag)
      if (response.status === 200 || response.data?.success === true || response.success === true) {
        const generated = response.data?.data?.generated_count || response.data?.generated_count || response.generated_count || 0;
        const failed = response.data?.data?.failed_count || response.data?.failed_count || response.failed_count || 0;
        alert(`✅ Bulk generation completed! Generated: ${generated}, Failed: ${failed}. Check PayrollSettings for breakdown.`);
        setBulkForm({ month: '', year: new Date().getFullYear(), emp_codes: [] });
        
        // Log the activity
        logCreate('Bulk Payslips Generated', `Generated ${generated} payslips for ${bulkForm.month} ${bulkForm.year}`);
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error occurred';
        alert(`❌ Error in bulk generation: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Bulk generate error:', error);
      
      // Handle network timeout/error gracefully
      if (!error.response) {
        alert('⚠️ Request may have timed out, but the bulk operation likely completed successfully. Please check PayrollSettings.');
      } else {
        const errorMsg = error.response.data?.message || error.response.data?.error || error.message || 'Network error occurred';
        alert(`❌ Error in bulk generation: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTaxRegime = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await payrollAPI.selectTaxRegime(taxRegimeForm);
      console.log('Tax regime response:', response);
      
      // Check if response indicates success (HTTP 200 OR success flag)
      if (response.status === 200 || response.data?.success === true) {
        alert('✅ Tax regime selected successfully!');
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error occurred';
        alert(`❌ Error selecting tax regime: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Tax regime error:', error);
      
      // Handle network timeout/error gracefully
      if (!error.response) {
        alert('⚠️ Request may have timed out, but the operation likely completed successfully.');
      } else {
        const errorMsg = error.response.data?.message || error.response.data?.error || error.message || 'Network error occurred';
        alert(`❌ Error selecting tax regime: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (request) => {
    setLoading(true);
    try {
      const response = await payrollAPI.approvePayslipRequest({
        emp_code: request.emp_code,
        month: request.month,
        year: request.year
      });
      console.log('Approve request response:', response);
      
      // Check if response indicates success (HTTP 200 OR success flag)
      if (response.status === 200 || response.data?.success === true) {
        alert('✅ Payslip request approved!');
        fetchPayslipRequests();
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error occurred';
        alert(`❌ Error approving request: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Approve request error:', error);
      
      // Handle network timeout/error gracefully
      if (!error.response) {
        alert('⚠️ Request may have timed out, but the operation likely completed successfully.');
        fetchPayslipRequests();
      } else {
        const errorMsg = error.response.data?.message || error.response.data?.error || error.message || 'Network error occurred';
        alert(`❌ Error approving request: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (request) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason || reason.trim() === '') return;
    
    setLoading(true);
    try {
      console.log('Rejecting request with reason:', reason);
      const response = await payrollAPI.rejectPayslipRequest({
        emp_code: request.emp_code,
        month: request.month,
        year: request.year,
        reason: reason.trim()
      });
      console.log('Reject request response:', response);
      
      // Check if response indicates success (HTTP 200 OR success flag)
      if (response.status === 200 || response.data?.success === true) {
        alert('✅ Payslip request rejected!');
        fetchPayslipRequests();
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error occurred';
        alert(`❌ Error rejecting request: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Reject request error:', error);
      
      // Handle network timeout/error gracefully
      if (!error.response) {
        alert('⚠️ Request may have timed out, but the operation likely completed successfully.');
        fetchPayslipRequests();
      } else {
        const errorMsg = error.response.data?.message || error.response.data?.error || error.message || 'Network error occurred';
        alert(`❌ Error rejecting request: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Payroll Management System
            </h1>
            <p className="text-gray-600 mt-1">Generate and manage employee payslips</p>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'payroll', label: 'Payroll Management' },
                { id: 'activity', label: 'Activity Logs' },
                { id: 'reports', label: 'Reports' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.id === 'activity' && <Clock size={16} />}
                  {tab.id === 'reports' && <ChartBarIcon size={16} />}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Activity Logs Tab */}
            {activeTab === 'activity' && (
              <ActivityLogs 
                moduleName="Salary Structure Builder" 
                activityTypes={activityTypes} 
              />
            )}
            
            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <ReportsWithDownload 
                moduleName="Salary Structure Builder" 
                reportTypes={reportTypes} 
              />
            )}

            {/* Payroll Management Tab */}
            {activeTab === 'payroll' && (
              <div>
                {/* Sub-tab Navigation */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="flex space-x-8">
                    {[
                      { id: 'generate', label: 'Generate Payslip' },
                      { id: 'bulk', label: 'Bulk Generate' },
                      { id: 'tax-regime', label: 'Tax Regime' },
                      { id: 'requests', label: 'Payslip Requests' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}

            {/* Generate Payslip Tab */}
            {(activeTab === 'generate' || (activeTab === 'payroll' && activeTab === 'generate')) && (
              <div className="max-w-md">
                <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Generate Individual Payslip
                </h2>
                <form onSubmit={handleGeneratePayslip} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Code
                    </label>
                    <input
                      type="text"
                      value={generateForm.emp_code}
                      onChange={(e) => setGenerateForm({ ...generateForm, emp_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Month
                    </label>
                    <select
                      value={generateForm.month}
                      onChange={(e) => setGenerateForm({ ...generateForm, month: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Month</option>
                      {months.map((month) => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year
                    </label>
                    <input
                      type="number"
                      value={generateForm.year}
                      onChange={(e) => setGenerateForm({ ...generateForm, year: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Generating...' : 'Generate Payslip'}
                  </button>
                </form>
              </div>
            )}

            {/* Bulk Generate Tab */}
            {activeTab === 'bulk' && (
              <div className="max-w-md">
                <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Bulk Generate Payslips
                </h2>
                <form onSubmit={handleBulkGenerate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Month
                    </label>
                    <select
                      value={bulkForm.month}
                      onChange={(e) => setBulkForm({ ...bulkForm, month: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Month</option>
                      {months.map((month) => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year
                    </label>
                    <input
                      type="number"
                      value={bulkForm.year}
                      onChange={(e) => setBulkForm({ ...bulkForm, year: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Codes (Optional)
                    </label>
                    <textarea
                      value={bulkForm.emp_codes.join(', ')}
                      onChange={(e) => setBulkForm({ 
                        ...bulkForm, 
                        emp_codes: e.target.value.split(',').map(code => code.trim()).filter(code => code)
                      })}
                      placeholder="Leave empty to generate for all active employees"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Generating...' : 'Generate Bulk Payslips'}
                  </button>
                </form>
              </div>
            )}

            {/* Tax Regime Tab */}
            {activeTab === 'tax-regime' && (
              <div className="max-w-md">
                <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Select Tax Regime
                </h2>
                <form onSubmit={handleSelectTaxRegime} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Regime
                    </label>
                    <select
                      value={taxRegimeForm.tax_regime}
                      onChange={(e) => setTaxRegimeForm({ ...taxRegimeForm, tax_regime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="NEW">New Tax Regime</option>
                      <option value="OLD">Old Tax Regime</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Financial Year
                    </label>
                    <input
                      type="text"
                      value={taxRegimeForm.financial_year}
                      onChange={(e) => setTaxRegimeForm({ ...taxRegimeForm, financial_year: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2024-2025"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Updating...' : 'Select Tax Regime'}
                  </button>
                </form>
              </div>
            )}

            {/* Payslip Requests Tab */}
            {activeTab === 'requests' && (
              <div>
                <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Payslip Requests
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Year
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Requested At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payslipRequests.map((request) => (
                        <tr key={request.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {request.emp_code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {request.employee_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {request.month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {request.year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              request.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {request.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.requested_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {request.status === 'PENDING' && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleApproveRequest(request)}
                                  disabled={loading}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request)}
                                  disabled={loading}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {payslipRequests.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No payslip requests found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom Reports Component with Download Functionality
const ReportsWithDownload = ({ moduleName, reportTypes }) => {
  const [selectedReportType, setSelectedReportType] = useState('');
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [payslipRequests, setPayslipRequests] = useState([]);
  const { logAction } = useAuditLog(moduleName);

  // Fetch activity data for reports
  const fetchActivityData = async () => {
    try {
      setLoading(true);
      // Fetch actual generated payslips data (same as PayrollSettings)
      const bulkResponse = await payrollAPI.getGeneratedPayslips();
      const payslips = bulkResponse?.data?.payslips || bulkResponse?.payslips || [];
      
      // Also fetch payslip requests for additional activity data
      const requestsResponse = await payrollAPI.getPayslipRequests();
      const requests = requestsResponse?.data?.data || requestsResponse?.data || [];
      setPayslipRequests(requests);
      
      // Create comprehensive activity data from actual payslips and requests
      const activities = [
        // Individual payslip generations from actual data
        ...payslips.map(payslip => ({
          type: 'Individual Payslip Generated',
          emp_code: payslip.emp_code,
          employee_name: payslip.employee_name,
          month: payslip.month || 'N/A',
          year: payslip.year || new Date().getFullYear(),
          status: 'COMPLETED',
          date: payslip.generated_at || payslip.created_at || new Date().toISOString(),
          action: 'Payslip Generated',
          gross_salary: payslip.gross_salary,
          net_salary: payslip.net_salary
        })),
        // Payslip requests
        ...requests.map(req => ({
          type: 'Individual Payslip Request',
          emp_code: req.emp_code,
          employee_name: req.employee_name,
          month: req.month,
          year: req.year,
          status: req.status,
          date: req.requested_at,
          action: 'Request Submitted'
        }))
      ];
      
      // Sort by date (newest first)
      activities.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setActivityData(activities);
    } catch (error) {
      console.error('Error fetching activity data:', error);
      setActivityData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityData();
  }, []);

  // Download Excel Report
  const downloadExcel = () => {
    if (activityData.length === 0) {
      alert('No activity data available for download');
      return;
    }

    const csvData = [
      ['S.No', 'Report Type', 'Activity Type', 'Employee Code', 'Employee Name', 'Month', 'Year', 'Status', 'Action', 'Date', 'Gross Salary', 'Net Salary'],
      ...activityData.map((activity, index) => [
        index + 1,
        selectedReportType || 'Payroll Activity Report',
        activity.type,
        activity.emp_code,
        activity.employee_name,
        activity.month,
        activity.year,
        activity.status,
        activity.action,
        new Date(activity.date).toLocaleDateString(),
        activity.gross_salary || 'N/A',
        activity.net_salary || 'N/A'
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReportType || 'payroll-activity-report'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    logAction('Report Downloaded', `Excel report downloaded: ${selectedReportType}`);
    alert('Excel report downloaded successfully!');
  };

  // Download PDF Report
  const downloadPDF = () => {
    if (activityData.length === 0) {
      alert('No activity data available for download');
      return;
    }

    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>${selectedReportType || 'Payroll Activity Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .summary { background-color: #f9f9f9; padding: 10px; margin: 10px 0; }
            .completed { color: green; font-weight: bold; }
            .pending { color: orange; font-weight: bold; }
            .rejected { color: red; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>${selectedReportType || 'Payroll Activity Report'}</h1>
          <div class="summary">
            <strong>Total Activities:</strong> ${activityData.length}<br>
            <strong>Generated On:</strong> ${new Date().toLocaleDateString()}<br>
            <strong>Completed Activities:</strong> ${activityData.filter(a => a.status === 'COMPLETED' || a.status === 'APPROVED').length}<br>
            <strong>Pending Activities:</strong> ${activityData.filter(a => a.status === 'PENDING').length}
          </div>
          <table>
            <tr>
              <th>S.No</th><th>Activity Type</th><th>Employee</th><th>Month/Year</th><th>Status</th><th>Date</th>
            </tr>
            ${activityData.map((activity, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${activity.type}</td>
                <td>${activity.employee_name} (${activity.emp_code})</td>
                <td>${activity.month} ${activity.year}</td>
                <td class="${activity.status.toLowerCase()}">${activity.status}</td>
                <td>${new Date(activity.date).toLocaleDateString()}</td>
              </tr>
            `).join('')}
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    
    logAction('Report Downloaded', `PDF report generated: ${selectedReportType}`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border">
        <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Payroll Activity Reports
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={selectedReportType}
              onChange={(e) => setSelectedReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Report Type</option>
              {reportTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              <strong>Available Activities:</strong> {activityData.length} records<br />
              <strong>Payslip Requests:</strong> {payslipRequests.length} requests
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={downloadExcel}
            disabled={loading || activityData.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium flex items-center gap-2"
          >
            <Download size={16} />
            {loading ? 'Loading...' : 'Download Excel'}
          </button>
          
          <button
            onClick={downloadPDF}
            disabled={loading || activityData.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 font-medium flex items-center gap-2"
          >
            <FileText size={16} />
            {loading ? 'Loading...' : 'Download PDF'}
          </button>
        </div>
        
        {/* Activity Preview Table */}
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">Loading...</td>
                </tr>
              ) : activityData.slice(0, 5).map((activity, index) => (
                <tr key={index}>
                  <td className="px-4 py-3 text-sm text-gray-900">{activity.type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{activity.employee_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{activity.month} {activity.year}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      activity.status === 'COMPLETED' || activity.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      activity.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {activity.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{new Date(activity.date).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {activityData.length > 5 && (
            <div className="text-center py-2 text-sm text-gray-500">
              Showing 5 of {activityData.length} activities. Download full report for complete data.
            </div>
          )}
        </div>
        
        {activityData.length === 0 && !loading && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800 text-sm">
              No payroll activity data available. Generate some payslips first to see activity reports.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryStructureBuilder;